import { lineString } from '@turf/helpers';
import turfSimplify from '@turf/simplify';
import isEqual from 'lodash.isequal';
import { MercatorCoordinate } from 'mapbox-gl';
import { LatLng } from '../../../types/Geo';

// Free draw tuning, with distances in screen pixels at the zoom the path is drawn at so drawing feels the same at every zoom
export type FreeDrawSettings = {
  // Mouse movement shorter than this is ignored while drawing, dropping the 1px stair-steps and hand jitter of raw mousemove events
  minPointDistancePx: number;
  // Douglas-Peucker tolerance applied when the path is saved; larger values straighten more of the line
  simplifyTolerancePx: number;
  // Chaikin corner-cutting passes applied after simplifying; each pass rounds corners further and roughly doubles the point count
  smoothingIterations: number;
  // Floor on the ground for the point spacing and tolerance, so zoomed far in they don't act on detail finer than the map data
  minGroundDistanceMeters: number;
  // The floor only applies below this zoom. A metre keeps growing in pixels as you zoom in (about 68px at zoom 22), so above it
  // the floor would throw away the detail someone zoomed in that far to draw
  groundFloorMaxZoom: number;
};

// Tuned on a webdev for TMT-1363
export const FREE_DRAW_SETTINGS: FreeDrawSettings = {
  minPointDistancePx: 1,
  simplifyTolerancePx: 1,
  smoothingIterations: 1,
  minGroundDistanceMeters: 1,
  groundFloorMaxZoom: 18
};

const TILE_SIZE = 512;

type Pixel = [number, number];

// Mercator coordinates run from 0 to 1 across the world, which Mapbox lays out at this many pixels across at the given zoom
const getWorldSize = (zoom: number) => TILE_SIZE * 2 ** zoom;

// World pixel coordinates at the given zoom, which is how Mapbox lays out a map that is not pitched or rotated
const toPixel = ([latitude, longitude]: LatLng, zoom: number): Pixel => {
  const { x, y } = MercatorCoordinate.fromLngLat({ lng: longitude, lat: latitude });
  const worldSize = getWorldSize(zoom);

  return [x * worldSize, y * worldSize];
};

const toLatLng = ([x, y]: Pixel, zoom: number): LatLng => {
  const worldSize = getWorldSize(zoom);
  const { lat, lng } = new MercatorCoordinate(x / worldSize, y / worldSize).toLngLat();

  return [lat, lng];
};

// How many pixels a distance on the ground spans at the given latitude and zoom
const metersToPixels = (meters: number, latitude: number, zoom: number) =>
  meters * MercatorCoordinate.fromLngLat({ lng: 0, lat: latitude }).meterInMercatorCoordinateUnits() * getWorldSize(zoom);

// The ground floor in pixels, or 0 at zooms where it no longer applies
const getGroundFloorPx = (settings: FreeDrawSettings, latitude: number, zoom: number) =>
  zoom < settings.groundFloorMaxZoom ? metersToPixels(settings.minGroundDistanceMeters, latitude, zoom) : 0;

const distanceSquared = ([x0, y0]: Pixel, [x1, y1]: Pixel) => (x1 - x0) ** 2 + (y1 - y0) ** 2;

// Douglas-Peucker via turf, as in addAutomaticHandles. It is given world pixels rather than lat/lng so the tolerance is in pixels in
// every direction; turf's math is plain x/y, while degrees of latitude and longitude span different distances
const simplify = (pixels: Pixel[], tolerance: number): Pixel[] => {
  // turf cleans the line first, which throws on a line that ends where it starts unless it has enough points to be a polygon, so
  // simplify without the closing point and put it back after
  const isClosed = isEqual(pixels[0], pixels[pixels.length - 1]);
  const line = lineString(isClosed ? pixels.slice(0, -1) : pixels);
  const simplified = turfSimplify(line, { tolerance, highQuality: true, mutate: true }).geometry.coordinates as Pixel[];

  return isClosed ? [...simplified, pixels[pixels.length - 1]] : simplified;
};

// Chaikin corner cutting, with the first and last pixels left in place
const smooth = (pixels: Pixel[], iterations: number): Pixel[] => {
  let smoothed = pixels;

  for (let iteration = 0; iteration < iterations && smoothed.length > 2; iteration++) {
    const next: Pixel[] = [smoothed[0]];

    for (let index = 0; index < smoothed.length - 1; index++) {
      const [x0, y0] = smoothed[index];
      const [x1, y1] = smoothed[index + 1];
      next.push([0.75 * x0 + 0.25 * x1, 0.75 * y0 + 0.25 * y1], [0.25 * x0 + 0.75 * x1, 0.25 * y0 + 0.75 * y1]);
    }

    next.push(smoothed[smoothed.length - 1]);
    smoothed = next;
  }

  return smoothed;
};

/**
 * @returns whether the cursor has moved far enough from the previous free draw point to add another
 */
export const hasMovedMinDistance = (previousPoint: LatLng, point: LatLng, zoom: number, settings = FREE_DRAW_SETTINGS): boolean => {
  const minDistancePx = Math.max(settings.minPointDistancePx, getGroundFloorPx(settings, previousPoint[0], zoom));

  return distanceSquared(toPixel(previousPoint, zoom), toPixel(point, zoom)) >= minDistancePx ** 2;
};

/**
 * Straightens the wobble out of a free draw path and then rounds its corners, keeping its endpoints where they are
 * @param zoom the zoom the path was drawn at, which sets how far a pixel of tolerance reaches on the ground
 */
export const smoothFreeDrawPath = (points: LatLng[], zoom: number, settings = FREE_DRAW_SETTINGS): LatLng[] => {
  if (points.length <= 2) {
    return points;
  }

  // A path spans little enough latitude that the ground distance of a pixel at its start holds along all of it
  const tolerancePx = Math.max(settings.simplifyTolerancePx, getGroundFloorPx(settings, points[0][0], zoom));
  const smoothed = smooth(
    simplify(
      points.map(point => toPixel(point, zoom)),
      tolerancePx
    ),
    settings.smoothingIterations
  );

  // Use the original endpoints rather than round-tripped ones so the path still meets the handle it was drawn from exactly
  return [points[0], ...smoothed.slice(1, -1).map(pixel => toLatLng(pixel, zoom)), points[points.length - 1]];
};
